import { DataTable } from '@/common/ui/data-table'
import { orderStatus, OrderT } from '@/common/types'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { Button } from '@/common/ui'
import useOrderReviewStore from './orderReviewStore'
import { ReviewOrderModal } from './reviewOrderModal'
import { CancelOrderSuccessModal } from './cancelOrderConfirmationModal'
import { SecurityHead } from '@/securities/securityHead.tsx'
import { ORDER_FAILURE_CODES } from '@/common/constants'
import { InfoIconTooltip } from '@/common/ui/tooltip'
import { OrderStatusBadge } from './OrderStatusBadge'
import { InfoIcon } from 'lucide-react'

type orderProps = {
    orders: OrderT[]
}

export function Orders(props: orderProps) {
    const { orders } = props
    const { setCurrentOrder } = useOrderReviewStore()
    const [reviewOrderModalOpen, setReviewOrderModalOpen] = useState<boolean>(false)
    const [cancelOrderSuccessModalOpen, setCancelOrderSuccessModalOpen] = useState<boolean>(false)

    const columns: ColumnDef<OrderT>[] = useMemo(
        () => [
            {
                accessorKey: 'securityCode',
                enableSorting: false,
                header: () => {
                    return (
                        <span className="w-full px-6 py-3 h-10 bg-stone-50 rounded-tl-lg rounded-bl-lg border-l border-t border-b border-border-divider inline-flex justify-start items-center gap-1 overflow-hidden">
                            Code
                        </span>
                    )
                },
                cell: ({ row }) => {
                    const securityCode = row.original.securityCode
                    return (
                        <SecurityHead
                            nameClassName="max-w-10 md:max-w-35 xl:max-w-40 2xl:max-w-max"
                            security={{
                                securityCode: securityCode,
                                securityName: row.original.companyName,
                            }}
                        />
                    )
                },
            },
            {
                accessorKey: 'orderSide',
                enableSorting: false,
                header: () => {
                    return (
                        <span className="w-full px-3 py-3 h-10 text-content-secondary font-semibold bg-stone-50 inline-flex justify-start items-center border-t border-b border-border-divider gap-1 overflow-hidden">
                            Type
                        </span>
                    )
                },
                cell: ({ row }) => {
                    return <span className="capitalize">{row.original.orderSide.toLowerCase()}</span>
                },
            },
            {
                accessorKey: 'units',
                enableSorting: false,
                header: () => {
                    return (
                        <span className="w-full px-3 py-3 h-10 bg-stone-50 inline-flex justify-start items-center border-t border-b border-border-divider gap-1 overflow-hidden">
                            Shares
                        </span>
                    )
                },
                cell: ({ row }) => {
                    if (row.original.status !== orderStatus['PARTIALLY FILLED']) {
                        return <span>{row.original.units}</span>
                    }

                    return (
                        <div>
                            <span className="text-black leading-5">{row.original.orderCompletionUnits}</span>
                            <span className="text-content-secondary leading-5">/{row.original.units} </span>
                        </div>
                    )
                },
            },
            {
                accessorKey: 'status',
                enableSorting: false,
                header: () => {
                    return (
                        <span className="w-full min-w-40 px-3 py-3 h-10 bg-stone-50 inline-flex justify-start items-center gap-1 border-t border-b border-border-divider overflow-hidden">
                            Status
                        </span>
                    )
                },
                cell: ({ row }) => {
                    const hasFailureCode = row.original.status === orderStatus.FAILED && row.original.failureCode
                    const failureInfo = hasFailureCode
                        ? ORDER_FAILURE_CODES[row.original.failureCode as keyof typeof ORDER_FAILURE_CODES]
                        : null

                    return (
                        <div className="flex items-center gap-2">
                            <OrderStatusBadge status={row.original.status}>
                                {failureInfo ? (
                                    <InfoIconTooltip
                                        size={16}
                                        infoClassName="text-sentiment-bad text-medium"
                                        contentClassName="self-stretch inline-flex flex-col justify-start items-start p-2 md:bg-stone-50/80 text-sentiment-bad gap-2"
                                    >
                                        <InfoIcon size={16} />
                                        <p className="text-sm font-medium font-['Inter'] leading-5">
                                            {failureInfo.title}
                                        </p>
                                        <p className="text-xs font-normal font-['Inter'] leading-5">
                                            {failureInfo.description}
                                        </p>
                                    </InfoIconTooltip>
                                ) : null}
                            </OrderStatusBadge>
                        </div>
                    )
                },
            },
            {
                accessorKey: 'trade',
                enableSorting: false,
                header: () => {
                    return (
                        <span className="w-full px-6 py-3 h-10 bg-stone-50 rounded-tr-lg rounded-br-lg border-r border-t border-b border-border-divider inline-flex justify-start items-center gap-1 overflow-hidden"></span>
                    )
                },
                cell: ({ row }) => {
                    return (
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setCurrentOrder(row.original)
                                    setReviewOrderModalOpen(true)
                                }}
                            >
                                {[
                                    orderStatus['IN MARKET'],
                                    orderStatus.REQUESTED,
                                    orderStatus['PARTIALLY FILLED'],
                                ].includes(row.original.status)
                                    ? 'Review'
                                    : 'View'}
                            </Button>
                        </div>
                    )
                },
            },
        ],
        [setCurrentOrder],
    )

    return (
        <>
            <DataTable
                classNameHeaderRow={'[&_tr]:border-none'}
                classNameHeader={'px-0 h-10'}
                columns={columns}
                data={orders}
                noDataMessage="No orders yet..."
            />
            <ReviewOrderModal
                open={reviewOrderModalOpen}
                onClose={() => setReviewOrderModalOpen(false)}
                onCancelOrder={() => {
                    setReviewOrderModalOpen(false)
                    setCancelOrderSuccessModalOpen(true)
                }}
            />
            <CancelOrderSuccessModal
                open={cancelOrderSuccessModalOpen}
                onClose={() => {
                    setCancelOrderSuccessModalOpen(false)
                    setCurrentOrder(null)
                }}
            />
        </>
    )
}
